import api from './api';

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDateString = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
};

export const dailyReviewService = {
    // Get projects with tasks for a specific date
    getProjects: async (date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/projects?date=${targetDate}`);
        return response.data;
    },

    // Get project daily review details
    getProjectReview: async (projectId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/project/${projectId}?date=${targetDate}`);
        return response.data;
    },

    // Get user allocation review for a project
    getUserAllocation: async (projectId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/project/${projectId}/user-allocation?date=${targetDate}`);
        return response.data;
    },

    // Get project users status (with/without work plans)
    getUsersStatus: async (projectId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/project/${projectId}/users-status?date=${targetDate}`);
        return response.data;
    },

    // Get project summary - sharp focused data
    getProjectSummary: async (projectId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/project/${projectId}/summary?date=${targetDate}`);
        return response.data;
    },
    // Get resource review list for sequential review
    getResourceReviewList: async (projectId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/project/${projectId}/resource-review-list?date=${targetDate}`);
        return response.data;
    },
    // Get detailed resource review for a single user
    getResourceReviewDetail: async (projectId, userId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/project/${projectId}/resource/${userId}/review?date=${targetDate}`);
        return response.data;
    },
    // Mark project as reviewed for a date
    markProjectAsReviewed: async (projectId, notes, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.post(`/api/manager/daily-review/project/${projectId}/mark-reviewed?date=${targetDate}`, {
            notes
        });
        return response.data;
    },
    // Add task to resource's work plan (manager action)
    addTaskToResourceWorkPlan: async (projectId, userId, taskId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.post(`/api/manager/daily-review/project/${projectId}/resource/${userId}/add-to-work-plan?date=${targetDate}`, {
            taskId
        });
        return response.data;
    },
    // Assign backlog task to resource (manager action)
    assignTaskToResource: async (projectId, userId, taskId) => {
        const response = await api.post(`/api/manager/daily-review/project/${projectId}/resource/${userId}/assign-task`, {
            taskId
        });
        return response.data;
    },
    // Unassign task from resource (manager action)
    unassignTaskFromResource: async (projectId, userId, taskId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.post(`/api/manager/daily-review/project/${projectId}/resource/${userId}/unassign-task?date=${targetDate}`, {
            taskId
        });
        return response.data;
    },
    // Send report as PDF via email
    sendReportEmail: async (projectId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.post(`/api/manager/daily-review/project/${projectId}/send-report-email?date=${targetDate}`);
        return response.data;
    },
    // Get previous day's notes or latest available notes
    getPreviousNotes: async (projectId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/project/${projectId}/notes/previous?date=${targetDate}`);
        return response.data; // Returns { date, notes, reviewedAt } or null
    },
    // Initiate review - creates review record
    initiateReview: async (projectId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.post(`/api/manager/daily-review/project/${projectId}/initiate-review?date=${targetDate}`);
        return response.data;
    },
    // Save notes without requiring overallAssessment (partial save)
    saveNotes: async (projectId, notes, date = null, options = {}) => {
        const targetDate = date || getTodayDateString();
        const config = {};
        
        // Support AbortController signal for request cancellation
        if (options.signal) {
            config.signal = options.signal;
        }
        
        const response = await api.post(`/api/manager/daily-review/project/${projectId}/save-notes?date=${targetDate}`, {
            notes
        }, config);
        return response.data;
    },
    // Get today's project outcome evaluation
    getTodayProjectOutcome: async (projectId, date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/project/${projectId}/today-outcome?date=${targetDate}`);
        return response.data;
    },

    // Get tasks and features for goal selection
    getGoalsData: async (projectId) => {
        const response = await api.get(`/api/manager/daily-review/project/${projectId}/goals-data`);
        return response.data;
    },
    // Get all resources with their projects for a specific date
    getResourcesWithProjects: async (date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/resources?date=${targetDate}`);
        return response.data;
    },
    // Get all goals across all projects for a specific date
    getAllGoals: async (date = null) => {
        const targetDate = date || getTodayDateString();
        const response = await api.get(`/api/manager/daily-review/all-goals?date=${targetDate}`);
        return response.data;
    }
};

export default dailyReviewService;

